import { useState, useEffect } from "react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, ResponsiveContainer, Legend,
} from "recharts";
import { BarChart3, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ["#2563EB", "#7C3AED", "#059669", "#D97706", "#DC2626", "#0891B2", "#65A30D", "#DB2777", "#EA580C"];

const STATUS_COLORS = {
  "Received": "#64748B",
  "Under Investigation": "#D97706",
  "Waiting for Approval": "#EA580C",
  "Repair in Progress": "#7C3AED",
  "Repaired": "#65A30D",
  "Replacement Approved": "#0891B2",
  "Replacement Dispatched": "#06B6D4",
  "Completed": "#059669",
  "Warranty Rejected": "#DC2626",
};

function ChartCard({ title, children, span = 1 }) {
  return (
    <div
      className={`bg-white rounded-xl border border-slate-200 p-6 ${span === 2 ? "lg:col-span-2" : ""}`}
    >
      <h3
        className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-5"
        style={{ fontFamily: "'Outfit', sans-serif" }}
      >
        {title}
      </h3>
      {children}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center h-48 text-slate-400">
      <BarChart3 className="w-10 h-10 mb-2 opacity-40" />
      <p className="text-sm">No data yet — create some tickets first</p>
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = () => {
    setLoading(true);
    axios
      .get(`${API}/analytics/overview`, { withCredentials: true })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const modelData = (data?.charger_models || []).map((d) => ({
    name: d._id || "Unknown", value: d.count,
  }));
  const warrantyData = (data?.warranty_types || []).map((d) => ({
    name: d._id || "Unknown", value: d.count,
  }));
  const monthlyData = (data?.monthly_volume || []).map((d) => ({
    name: d._id || "Unknown", count: d.count,
  }));
  const statusData = (data?.status_breakdown || []).map((d) => ({
    name: d._id || "Unknown",
    value: d.count,
    fill: STATUS_COLORS[d._id] || "#64748B",
  }));
  const serviceData = (data?.service_types || []).map((d) => ({
    name: d._id || "Unknown", value: d.count,
  }));

  const summaryCards = [
    { label: "Total Tickets", value: data?.total ?? 0, color: "text-blue-600", bg: "bg-blue-50" },
    { label: "Charger Models", value: modelData.length, color: "text-purple-600", bg: "bg-purple-50" },
    { label: "This Month", value: monthlyData[monthlyData.length - 1]?.count ?? 0, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Completed", value: statusData.find(s => s.name === "Completed")?.value ?? 0, color: "text-teal-600", bg: "bg-teal-50" },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1
            className="text-2xl font-bold text-slate-900 tracking-tight"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Analytics
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">Service centre insights & patterns</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchData}
          disabled={loading}
          data-testid="refresh-analytics-btn"
          className="text-slate-600"
        >
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {summaryCards.map(({ label, value, color, bg }) => (
          <div
            key={label}
            className="bg-white rounded-xl border border-slate-200 p-5 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200"
          >
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{label}</p>
            {loading ? (
              <div className="h-8 w-12 bg-slate-100 rounded animate-pulse" />
            ) : (
              <p className={`text-3xl font-bold tracking-tight ${color}`}>{value}</p>
            )}
          </div>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-72 bg-slate-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Monthly Volume */}
          <ChartCard title="Monthly Ticket Volume" span={2}>
            {monthlyData.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={monthlyData} margin={{ top: 0, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "12px" }}
                    formatter={(v) => [v, "Tickets"]}
                  />
                  <Bar dataKey="count" fill="#2563EB" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Charger Model Breakdown */}
          <ChartCard title="Tickets by Charger Model">
            {modelData.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={modelData} margin={{ top: 0, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748B" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "12px" }}
                    formatter={(v) => [v, "Tickets"]}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {modelData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Warranty Type Distribution */}
          <ChartCard title="Warranty Type Distribution">
            {warrantyData.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={warrantyData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, percent }) =>
                      `${name.split(" ")[0]} ${(percent * 100).toFixed(0)}%`
                    }
                    labelLine={false}
                  >
                    {warrantyData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "12px" }}
                    formatter={(v, name) => [v, name]}
                  />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: "11px" }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Status Breakdown */}
          <ChartCard title="Status Breakdown" span={2}>
            {statusData.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={statusData}
                  layout="vertical"
                  margin={{ top: 0, right: 20, left: 10, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
                  <YAxis
                    dataKey="name"
                    type="category"
                    tick={{ fontSize: 10, fill: "#64748B" }}
                    width={160}
                  />
                  <Tooltip
                    contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "12px" }}
                    formatter={(v) => [v, "Tickets"]}
                  />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {statusData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Service Type */}
          <ChartCard title="Repair vs Replacement">
            {serviceData.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={serviceData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    <Cell fill="#2563EB" />
                    <Cell fill="#7C3AED" />
                  </Pie>
                  <Tooltip
                    contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "12px" }}
                  />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: "11px" }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>
      )}
    </div>
  );
}
